import { Required, IsString, IsStringOrNumber, DataValidator, transformKeys, ValidateNested, IsNumber } from "@/utils/type-vaildator";
import api from "./api";

// 响应数据类型（参考 PushInfoOriginDataRecordListResponse 的结构）
interface AgentUserSubinfoGetResponse {
  data: any;
}

export class SubscribeInfoData {
  @Required("videoSubscribe 不能为空")
  @IsNumber("videoSubscribe 必须是数字")
  videoSubscribe: number;

  @Required("articleSubscribe 不能为空")
  @IsNumber("articleSubscribe 必须是数字")
  articleSubscribe: number;

  @Required("aiSubscribe 不能为空")
  @IsNumber("aiSubscribe 必须是数字")
  aiSubscribe: number;

  @Required("blogSubscribe 不能为空")
  @IsNumber("blogSubscribe 必须是数字")
  blogSubscribe: number;

  @Required("momentsSubscribe 不能为空")
  @IsNumber("momentsSubscribe 必须是数字")
  momentsSubscribe: number;

  @Required("graphicSubscribe 不能为空")
  @IsNumber("graphicSubscribe 必须是数字")
  graphicSubscribe: number;
  
  @IsNumber("chatSubscribe 必须是数字")
  chatSubscribe: number;
}

export class ApplyInputTypeInfo {
  @IsString("articleApplyInputType 必须是字符串")
  articleApplyInputType: string;

  @IsString("blogApplyInputType 必须是字符串")
  blogApplyInputType: string;

  @IsString("graphicApplyInputType 必须是字符串")
  graphicApplyInputType: string;

  @IsString("momentsApplyInputType 必须是字符串")
  momentsApplyInputType: string;

  @IsString("videoApplyInputType 必须是字符串")
  videoApplyInputType: string;
}

// 原始数据校验类（接口字段驼峰化后进行校验）
export class AgentUserSubinfoRawData {
  // @Required("id 不能为空")
  @IsStringOrNumber("id 必须是字符串或数字")
  id: string | number;

  @IsString("name 必须是字符串")
  name?: string;

  @IsString("intro 必须是字符串")
  intro?: string;

  @IsString("avatar 必须是字符串")
  avatar?: string;

  @IsStringOrNumber("createdAt 必须是字符串或数字")
  createdAt?: string | number;

  @IsStringOrNumber("updatedAt 必须是字符串或数字")
  updatedAt?: string | number;

  // sub_name -> subName（transformKeys 自动驼峰化）
  @IsString("subName 必须是字符串")
  subName?: string;

  @IsString("subIntro 必须是字符串")
  subIntro?: string;

  @IsString("subAvatar 必须是字符串")
  subAvatar?: string;

  @IsString("userIntroduction 必须是字符串")
  subIntroduction?: string;

  @Required("subscribeInfo 不能为空")
  @ValidateNested(SubscribeInfoData)
  subscribeInfo?: SubscribeInfoData;

  @Required("applyInputTypeInfo 不能为空")
  @ValidateNested(ApplyInputTypeInfo)
  applyInputTypeInfo?: ApplyInputTypeInfo;

  @Required("pushSettings 不能为空")
  pushSettings?: { [key: number]: number };
}

// 返回数据类型（驼峰化并按 fromJSON 约定字段命名）
export interface AgentModel {
  id: string;
  name: string;
  description: string;
  avatarUrl: string;
  createdAt: number | null;
  updatedAt: number | null;
  userName: string;
  userIntro: string;
  userIntroduction: string;
  userAvatarUrl: string;
  subscribeInfo: SubscribeInfoData;
  applyInputTypeInfo: ApplyInputTypeInfo;
  pushSettings: { [key: number]: number }
}

const parseDateToMs = (v: any): number | null => {
  if (v == null) return null;
  if (typeof v === "number") {
    // 兼容秒或毫秒
    return v > 1e12 ? v : v * 1000;
  }
  const s = String(v);
  const asInt = parseInt(s, 10);
  if (!isNaN(asInt)) return asInt > 1e12 ? asInt : asInt * 1000;
  const t = Date.parse(s);
  return isNaN(t) ? null : t;
};

const transformAndValidate = (response: AgentUserSubinfoGetResponse): AgentUserSubinfoRawData => {
  const transformed = transformKeys(response);
  try {
    return DataValidator.validateOrThrow(transformed, AgentUserSubinfoRawData);
  } catch (error: any) {
    console.error("AgentUserSubinfo data validation failed:", error.message);
    throw error;
  }
};

export const getAgentById = async (id: string): Promise<AgentModel> => {
  if (!id || !id.trim()) {
    throw new Error("agentId cannot be empty");
  }

  const res = await api.post<AgentUserSubinfoGetResponse>("/agent/user/subinfo/get", { agent_id: id });
  const raw = transformAndValidate(res.data);

  const model: AgentModel = {
    id: String(raw.id ?? ""),
    name: String(raw.name ?? ""),
    description: String(raw.intro ?? ""),
    avatarUrl: String(raw.avatar ?? ""),
    createdAt: parseDateToMs(raw.createdAt ?? null),
    updatedAt: parseDateToMs(raw.updatedAt ?? null),
    userName: String(raw.subName ?? ""),
    userIntro: String(raw.subIntro ?? ""),
    userIntroduction: String(raw.subIntroduction ?? ""),
    userAvatarUrl: String(raw.subAvatar ?? ""),
    subscribeInfo: raw.subscribeInfo ?? {} as SubscribeInfoData,
    applyInputTypeInfo: raw.applyInputTypeInfo ?? {} as ApplyInputTypeInfo,
    pushSettings: raw.pushSettings ?? {},
  };

  return model;
};
